import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import CustomButton from '@/components/Common/Button';
import { useNavigate, useSearchParams } from "react-router-dom";

import { store, useAppSelector } from '@/app/store';
import { numericSort, padZeros, RoleContext } from '@/app/api/common';
import { checkNoError, getUrl, getField } from '@/features/channels/channelsSlice';
import { copyProjectInfo, getPlatformDeviceCount, getProjectById, HotelInfo } from '@/features/projects/projectsSlice';
import { isLightRow } from '@/features/projects/mwLight';
import { formatExpiryDate } from '@/components/Common/UrlsEditor';
import { getProjectGroupById, getUngroupedProjects, ProjectGroupWithStats, sumRoomStats } from '@/features/projectGroups/projectGroupsSlice';
import { usePageSubscription } from '@/app/websocket/hooks';

import FlexRow from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import Input from '@/components/Common/Input';
import MenuItem from '@/components/Common/MenuItem';
import { DeleteModal, Modal as CommonModal, RightBar } from '@/components/Common/Modals';
import ProjectGroupEditor from './ProjectGroupEditor';

export interface NumberTextProps {
  children?:			React.ReactNode;
}

const NumberText = (props: NumberTextProps) => {
  return (
  <span className="text-[#1B69AA]">
    { props.children }
  </span>
  );
};

export interface ProjectRow {
  id:					string;
  name:					string;
  used_limit?:			string;
  rooms_max:     string;
  rooms_active:  string;
  rooms_live:    string;
  location:				string;
  bexio_url:			string;
  partner:			string;
  chainmanager:		string;
  manager:			string;
  contact:				string;
  tags:					string;
  state:				string;
  device_count?:		string;
  // §2.3 — an MW light row: no stats, no options, and the click opens
  // /mw_light/:id instead of the project page.
  mw_light:				boolean;
  // #93 §2.3 — admins only; the server sends neither key to anyone else.
  expired?:				boolean;
  expires_at?:			number;
  requestDelete:		any;
  requestClone:			any;
  // Task-80 §4.1 — a group row: one line for every member, opening the
  // drill-down instead of a project.
  is_group?:			boolean;
  group_id?:			string;
  requestEditGroup?:	any;
}

export interface ProjectsProps {
  hideContainer?: boolean;  // e.g., 'channelList', 'main', etc.
  csvExport?: number;
  csvFile?: string;
  // When set (platform drill-down), append a column with the number of
  // devices of that platform each project has (stats.rooms.platforms cumul).
  platformFilter?: string;
}
const Projects = (props: ProjectsProps) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const projects = useAppSelector((state) => state.projects);
  const tags = useAppSelector((state) => state.tags.tags);
  const projectGroups = useAppSelector((state) => state.projectGroups);
  const role = React.useContext(RoleContext);

  // Task-80 §4.1 — /projects?group=<uuid> is the drill-down into one group.
  // The embedded lists (hideContainer) never group.
  const [searchParams, setSearchParams] = useSearchParams();
  const groupId = props.hideContainer ? null : searchParams.get('group');
  const activeGroup = getProjectGroupById(groupId || undefined, projectGroups.groups);
  // 'new', a group id, or null
  const [editGroup, setEditGroup] = React.useState(null as string | null);
  const requestEditGroup = React.useRef((id: string) => ("" as any));

  // §3.6 — project_group events reach every global subscriber. The server
  // takes a {type:'project_group'} subscription for a hotel-scoped one and
  // refuses it without a scope_id, so subscribe to 'global' instead.
  usePageSubscription(props.hideContainer ? [] : [{ type: 'global' }]);

  const canWriteGroups = role.admin || role?.privilages?.includes("CreateProjects");
  
  const [cols, setCols] = React.useState([] as any[]);
  const [rows, setRows] = React.useState([] as ProjectRow[]);
  
  const [deleteProject, setDeleteProject] = React.useState(null as HotelInfo | null);
  const [cloneProject, setCloneProject] = React.useState(null as HotelInfo | null);
  const [cloneName, setCloneName] = React.useState("");
  const [cloneTried, setCloneTried] = React.useState(false);
  const [csvExport, setCsvExport] = React.useState(0);
  const requestDelete = React.useRef((id: string) => ("" as any));
  const requestClone = React.useRef((id: string) => ("" as any));

  React.useEffect(() => {
    if (!props.hideContainer) {
      store.clearChannelListProjects();
    }
  }, [props.hideContainer]);

  React.useEffect(() => {
    if (role.admin || role?.privilages?.includes("ViewTags")) {
      store.getTags();
    }
    store.getProjects();
    if (!props.hideContainer)
      store.getProjectGroups();
  }, []);

  React.useEffect(() => {
    if (props.hideContainer)
      return;

    store.enterProjectGroup(groupId);
  }, [groupId, props.hideContainer]);

  // ?edit=1 (the "set by group" link on a project) opens the editor at once.
  React.useEffect(() => {
    if (	!groupId ||
        	searchParams.get('edit') !== '1'
       )
      return;

    setEditGroup(groupId);
    setSearchParams({ group: groupId }, { replace: true });
  }, [groupId, searchParams]);

  requestEditGroup.current = (id: string) => setEditGroup(id);

  React.useEffect(() => {
    const toolTip = (props: any): string => {
      const ret =	props.value
		  			?	props.value.toString()
		  			:	"";
      
      //console.log("Projects/useEffect/toolTip/ret=" + ret + "/props=", props);
      
      return ret;
    };
    
    const rws: ProjectRow[] = [];
    setCols([
    {
        field:			"id",
        headerName:		t("projects:grid:id"),
        comparator:		groupsFirstSort,
        cellRenderer:	IdCell,
        sort:			"asc",
        flex:			0,
        minWidth:		80,
        maxWidth:		120,
        pinned:			'left',
        lockPinned:		true,
        cellStyle:		{ backgroundColor: '#ffffff' },
    },
    {
        field:			"name",
        headerName:		t("projects:grid:name"),
        flex:			1,
        minWidth:		200,
        //maxWidth:		300,
        cellRenderer:	NameCell,
    },
    {
        field:			"rooms_max",
        headerName:		t("projects:grid:rooms_max"),
        minWidth:		10,
        maxWidth: 100
    },
    {
        field:			"rooms_active",
        headerName:		t("projects:used"),
        flex:			0,
        minWidth:		10,
        maxWidth: 100
    },
    {
        field:			"rooms_live",
        headerName:		t("projects:grid:rooms_live"),
        minWidth:		10,
        maxWidth: 100
    },
    ...(
      props.platformFilter
      ? [{
          field:			"device_count",
          headerName:		t("projects:platforms:devices"),
          comparator:		numericSort,
          flex:			0,
          minWidth:		10,
          maxWidth: 120
        }]
      : []
    ),
        /*{
        field:			"location",
        headerName:		t("projects:grid:location"),
        flex:			0,
        minWidth:		100,
        maxWidth:		150,
    },*/
    ...(
     	role.admin
		?	[{
				field:			"bexio_url",
				headerName:		t("projects:grid:bexio_url"),
				flex:			0,
				minWidth:		100,
				maxWidth:		140,
				tooltipValueGetter:		toolTip,
			}]
		:	[]
    ),
    ...(
      role.admin || role?.privilages?.includes("SeeAllProjects")
      ? [{
          field:			"partner",
          headerName:		t("projects:grid:partner"),
          flex:			0,
          minWidth:		100,
          maxWidth:		150,
        },
        {
          field:			"chainmanager",
          headerName:		t("projects:grid:chainmanager"),
          flex:			0,
          minWidth:		100,
          maxWidth:		150,
        },
        {
          field:			"manager",
          headerName:		t("projects:grid:manager"),
          flex:			0,
          minWidth:		100,
          maxWidth:		150,
        }]
      : []
    ),
    ...(
      role.admin
      ? [{
          field:			"tags",
          headerName:		t("tags:tags"),
          flex:			1,
          minWidth:		150,
        }]
      : []
    ),
    /*{
        field:			"contact",
        headerName:		t("projects:grid:contact"),
        flex:			0,
        minWidth:		150,
        maxWidth:		200,
    },*/
    ...(
      !props.hideContainer && (role.admin || role?.privilages?.includes("EditProjectSettings") || role?.privilages?.includes("DeleteProjects"))
      ? [{
          field:			"options",
          headerName:		t("projects:grid:options"),
          flex:			0,
          filter:			false,
          minWidth:		90,
          maxWidth:		90,
          cellRenderer:	OptionsCell,
        }]
      : []
    ),
    ]);
    // When hideContainer is true (searchproject context), only show channelListProjects, don't fall back to all projects
    const filtered = props.hideContainer || (projects.channelListProjects && projects.channelListProjects.length > 0);
    const grouping = !filtered && !props.platformFilter;
    // Task-80 §4.1 — the drill-down shows the group's members; the top level
    // shows the groups plus the projects outside them.
    const projectsList: HotelInfo[] = props.hideContainer
      ? (projects.channelListProjects || [])
      : filtered
        ? projects.channelListProjects
        : groupId
          ? (projectGroups.active === groupId && projectGroups.members ? projectGroups.members : [])
          : grouping
            ? getUngroupedProjects(projects.projects, projectGroups.groups)
            : projects.projects;

    if (grouping && !groupId) {
      for (const group of projectGroups.groups) {
        rws.push(getGroupRow(group, requestDelete, requestClone, requestEditGroup));
      }
    }
    // Build a set of valid tag names from the global tags list
    const validTags = new Set(tags?.map((t: any) => t.tag) || []);

    for (let proj of projectsList) {
      // Map project tags to their tag names, filtering out deleted tags
      const tagDescriptions = proj.tags && proj.tags.length > 0
        ? proj.tags
            .map((t: any) => t.tag || '')
            .filter((tag: string) => tag !== '' && validTags.has(tag))
            .join(', ')
        : '';

      // §3.1 — a light row carries `stats: null` and `urls: []`, so every
      // figure and every executive cell is deliberately blank rather than a
      // row of zeroes that would read as a hotel with no rooms.
      const light = isLightRow(proj);

      const row: ProjectRow = {
      	id:					proj.id,
		name:				proj.name,
    rooms_max: proj.stats ? padZeros(proj.stats.rooms.allocated, 3):"" ,
    rooms_active:  proj.stats ? padZeros(proj.stats.rooms.created, 3): " " ,
    rooms_live: proj.stats ? padZeros(proj.stats.rooms.connected, 3) : '',
		location:			getField(proj.urls, "location"),
		bexio_url:			getField(proj.urls, "bexio_url"),
		partner:			proj.stats?.executives?.partner?.join(", ") || "",
		chainmanager:		getChainManagerCell(proj),
		manager:			proj.stats?.executives?.manager?.join(", ") || "",
		contact:			getField(proj.urls, "contact"),
		tags:				tagDescriptions,
		state:				proj.state || "enabled",
		device_count:		props.platformFilter
							?	padZeros(getPlatformDeviceCount(proj, props.platformFilter), 3)
							:	undefined,
		mw_light:			light,
		expired:			role.admin ? !!proj.expired : false,
		expires_at:			role.admin ? proj.expires_at : undefined,
		requestDelete:		requestDelete,
		requestClone:		requestClone,
      };

      rws.push(row);
    }

    setRows(rws);
  }, [projects.projects, projects.channelListProjects, t, role, props.hideContainer, props.platformFilter, tags, projectGroups.groups, projectGroups.active, projectGroups.members, groupId]);
  
  const handleCellClick = (evt: any) => {
    console.log("Projects/handleCellClick/evt=", evt);
    if (evt.colDef.field == "options")
      return;

    if (evt?.data?.is_group) {
      setSearchParams({ group: evt.data.group_id });
      return;
    }

    const id = encodeURIComponent(evt?.data?.id);

    // §2.3 — an MW light row NEVER opens the project page: that read answers
    // 403 for this user.
    navigate(	evt?.data?.mw_light
    			?	`/mw_light/${id}`
    			:	`/projects/${id}`
    		);
  };
  
  const processDelete = async (obj: Object, answer: string) => {
    setDeleteProject(null);
    
    if (answer !== "yes") {
      return;
    }
    
    if (!deleteProject)
      return;
    
    const proj = getProjectById(deleteProject.id, projects.projects);
    if (!proj)
      return;
    
    let project = copyProjectInfo(proj);
    if (!project)
      return;
    
    project.to_delete = true;
    const mess = await store.setProject(project);
    console.log("Projects/processDelete/mess=", mess);
    
    if (!checkNoError(mess))
      return;
  };
  
  requestDelete.current = React.useCallback((id: string) => {
    const proj = getProjectById(id, projects.projects);
    setDeleteProject(proj);
  }, [projects.projects, setDeleteProject]);

  requestClone.current = React.useCallback((id: string) => {
    const proj = getProjectById(id, projects.projects);
    setCloneProject(proj);
    setCloneName(proj ? proj.name + " (Copy)" : "");
    setCloneTried(false);
  }, [projects.projects]);

  const processClone = async () => {
    if (!cloneProject)
      return;

    if (!cloneName || cloneName.trim() === "") {
      setCloneTried(true);
      return;
    }

    const mess = await store.cloneProject(cloneProject.id, cloneName.trim());
    if (!checkNoError(mess))
      return;

    setCloneProject(null);
    setCloneName("");
    setCloneTried(false);
  };
  
  // The flat /hotels.json list holds every project exactly once, so its sums
  // never double count (§4.1); the drill-down sums only the members.
  const groupMembers = groupId && projectGroups.active === groupId && projectGroups.members
                       ? projectGroups.members
                       : [];
  const headerCount = groupId ? groupMembers.length : projects.projects.length;
  const headerStats = groupId ? sumRoomStats(groupMembers) : projects.stats;

  return (
  <React.Fragment>
    {!props.hideContainer &&
    <FlexRow direction="column"
	         align="start"
	         className="pt-[32px] px-[32px] bg-white sticky top-[64px] z-10">
      {groupId
       ? <FlexRow className="w-full justify-between">
           <FlexRow justify="start"
                    className="text-[32px]/[48px] gap-[8px]">
             <span className="cursor-pointer text-[#1B69AA]"
                   onClick={() => setSearchParams({})}>
               { t('projects:groups:breadcrumb_all') }
             </span>
             <span className="material-icons !text-[28px] text-[--Dark-Grey]">
               chevron_right
             </span>
             <span>
               { activeGroup ? activeGroup.name : '' }
             </span>
           </FlexRow>
         {canWriteGroups && activeGroup &&
           <CustomButton caption={t('projects:groups:edit')}
                         icon="edit_note"
                         className="uppercase"
                         onClick={() => setEditGroup(activeGroup.id)} />
         }
         </FlexRow>
       : <div className="text-[32px]/[48px]">
           { t('projects:caption') }
         </div>
      }
      <div>
        <NumberText>
          { headerCount }
        </NumberText>
        { ' ' +
          t('projects:projects') +
          ' - '
        }
        <NumberText>
          { headerStats.allocated }
        </NumberText>
        { ' ' +
          t('projects:units') +
          ' - '
        }
        <NumberText>
          { headerStats.created}
        </NumberText>
        { ' ' +
          t('projects:used')+
          ' - '
        }
        <NumberText>
          { headerStats.connected }
        </NumberText>
        { ' ' +
          t('projects:connected') 
        }
      </div>
      <Divider className="my-[8px]" />
    </FlexRow>
    }
    <FlexRow direction="column"
             align="start"
             className="py-[16px] px-[32px]">
      <FlexRow className="w-full justify-between mb-[16px]">
        {((role.admin && !props.hideContainer) ||(!props.hideContainer && role?.privilages?.includes("CreateProjects"))) &&
          <Button className="bg-[#1B69AA] font-bold rounded-[5px] px-2"
                  onClick={() => navigate('/projects/__new__')}>
            <FlexRow justify="center" className="gap-[4px]">
              <div className="material-icons !text-white !text-[16px]">
                add
              </div>
              <div className="m14 text-white leading-normal">
                { t('projects:new') }
              </div>
            </FlexRow>
          </Button>
        }
        {!props.hideContainer && !groupId && canWriteGroups &&
          <Button className="bg-[#1B69AA] font-bold rounded-[5px] px-2 mr-auto ml-[16px]"
                  onClick={() => setEditGroup('new')}>
            <FlexRow justify="center" className="gap-[4px]">
              <div className="material-icons !text-white !text-[16px]">
                add
              </div>
              <div className="m14 text-white leading-normal uppercase">
                { t('projects:groups:add') }
              </div>
            </FlexRow>
          </Button>
        }
        { !props.hideContainer && rows && rows.length > 0 &&
          <Button className="bg-[#1B69AA] font-bold rounded-[5px] px-2"
                  onClick={() => setCsvExport(csvExport + 1)}>
            <FlexRow justify="center" className="gap-[4px]">
              <div className="material-icons !text-white !text-[16px]">
                share
              </div>
              <div className="m14 text-white leading-normal">
                { t('channel_lists:options:export') }
              </div>
            </FlexRow>
          </Button>
        }
      </FlexRow>
      <Grid rowData={rows}
            columnDefs={cols}
            filter={true}
            showPages={true}
            pageSize={10}
            maxHeight={props.hideContainer ? undefined : "calc(100vh - 280px)"}
            onCellClicked={handleCellClick}
            csvFile={props.csvFile || 'projects.csv'}
            csvExport={props.csvExport || csvExport} />
    </FlexRow>
    {/* An existing group opens only once the list has it — before that, a
        null would read as "new group". */}
    {editGroup !== null && (editGroup === 'new' || getProjectGroupById(editGroup, projectGroups.groups)) &&
      <RightBar onOpenChange={(open: boolean) => { if (!open) setEditGroup(null); }}>
        <ProjectGroupEditor group={editGroup === 'new' ? null : getProjectGroupById(editGroup, projectGroups.groups)}
                            onClose={() => setEditGroup(null)} />
      </RightBar>
    }
    {deleteProject  &&
      <DeleteModal title={t('projects:delete_project:title')}
                   message={t('projects:delete_project:message_p', {p: deleteProject.id, name: deleteProject.name})}
                   onResult={processDelete}>
        <>
        </>
      </DeleteModal>
    }
    {cloneProject &&
      <CommonModal title={t('projects:clone_project_modal:title')}
                   message={t('projects:clone_project_modal:message', {name: cloneProject.name, id: cloneProject.id})}
                   onResult={() => { setCloneProject(null); setCloneName(""); setCloneTried(false); }}>
        <FlexRow direction="column" align="start" className="gap-[16px] pb-[16px]">
          <Input label={t('projects:clone_project_modal:name')}
                 placeholder={t('projects:clone_project_modal:name_placeholder')}
                 isInvalid={cloneTried && (!cloneName || cloneName.trim() === "")}
                 errorMessage={t('projects:clone_project_modal:name_invalid')}
                 value={cloneName}
                 onValueChange={(v: string) => setCloneName(v)} />
        </FlexRow>
        <FlexRow justify="end" className="gap-[16px]">
          <CustomButton caption={t('modals:warning:cancel')}
                  onClick={() => { setCloneProject(null); setCloneName(""); setCloneTried(false); }}
                  layout="white" />
          <CustomButton caption={t('projects:clone_project_modal:clone')}
                  icon="content_copy"
                  onClick={() => processClone()} />
        </FlexRow>
      </CommonModal>
    }
  </React.Fragment>
  );
};

// Task-80 §4.1 — one row per group: the server's summed stats.rooms, the
// group's chain managers, and the member count after the name.
const getGroupRow = (group: ProjectGroupWithStats, requestDelete: any, requestClone: any, requestEditGroup: any): ProjectRow => {
  const rooms = group.stats?.rooms;

  return {
    id:					"",
    name:				group.name + " (" + (group.projects || []).length + ")",
    rooms_max:			rooms ? padZeros(rooms.allocated, 3) : "",
    rooms_active:		rooms ? padZeros(rooms.created, 3) : "",
    rooms_live:			rooms ? padZeros(rooms.connected, 3) : "",
    location:			"",
    bexio_url:			"",
    partner:			"",
    chainmanager:		(group.settings?.chain_manager || []).join(", "),
    manager:			"",
    contact:			"",
    tags:				"",
    state:				"enabled",
    mw_light:			false,
    requestDelete:		requestDelete,
    requestClone:		requestClone,
    is_group:			true,
    group_id:			group.id,
    requestEditGroup:	requestEditGroup,
  };
};

// The project's own chain managers, then what it inherits from its group
// (§2.2). Display only — the two lists are never merged in a save.
const getChainManagerCell = (proj: HotelInfo): string => {
  const names = [...(proj.stats?.executives?.chain_manager || [])];

  for (const name of proj.stats?.group_settings?.chain_manager || []) {
    if (names.indexOf(name) < 0)
      names.push(name);
  }

  return names.join(", ");
};

// Groups stay on top in either direction; projects sort by number as before.
const groupsFirstSort = (a: string, b: string, nodeA: any, nodeB: any, isDescending: boolean) => {
  const ga = !!nodeA?.data?.is_group;
  const gb = !!nodeB?.data?.is_group;

  if (ga !== gb)
    return (ga ? -1 : 1) * (isDescending ? -1 : 1);

  if (ga)
    return (nodeA.data.name || "").localeCompare(nodeB.data.name || "");

  return numericSort(a, b);
};

const IdCell = (props: any) => {
  if (!props.data?.is_group)
    return props.value ? props.value.toString() : "";

  return (
  <span className="material-icons !text-[18px] text-[#1B69AA] align-middle">
    folder
  </span>
  );
};

// The project name, with the "MW light" badge on a light row (§2.3). The badge
// is what tells a user who is a Manager elsewhere that THIS hotel opens a room
// list with a Checkout button and nothing else.
const NameCell = (props: any) => {
  const { t } = useTranslation();

  const name =	props.value
				?	props.value.toString()
				:	"";

  // #93 §2.3 — red "Expired" once the server says so; grey "Expires {date}"
  // when the date is less than 7 days away.
  const expiresAt = props.data?.expires_at || 0;
  const expired = !!props.data?.expired;
  const expiresSoon =	!expired &&
  						expiresAt > 0 &&
  						expiresAt - Math.floor(Date.now() / 1000) < 7 * 86400;

  if (	!props.data?.mw_light &&
  		!expired &&
  		!expiresSoon
     )
    return name;

  return (
  <FlexRow justify="start"
           className="h-full gap-[8px]">
    <span className="truncate">
      { name }
    </span>
  {props.data?.mw_light &&
    <span className="shrink-0 rounded-full bg-[#E8F0F8] text-[#1B69AA] text-[11px] font-medium px-[8px] py-[2px] whitespace-nowrap">
      { t('mw_light:badge') }
    </span>
  }
  {expired &&
    <span className="shrink-0 rounded-full bg-[#FDECEA] text-[#B3261E] text-[11px] font-medium px-[8px] py-[2px] leading-[16px] whitespace-nowrap"
          title={expiresAt > 0 ? t('projects:grid:expired_tooltip', { date: formatExpiryDate(expiresAt) }) : undefined}>
      { t('projects:grid:expired') }
    </span>
  }
  {expiresSoon &&
    <span className="shrink-0 rounded-full bg-[#EEF0F3] text-[--Dark-Grey] text-[11px] font-medium px-[8px] py-[2px] leading-[16px] whitespace-nowrap"
          title={t('projects:grid:expires_tooltip', { date: formatExpiryDate(expiresAt) })}>
      { t('projects:grid:expires', { date: new Date(expiresAt * 1000).toLocaleDateString() }) }
    </span>
  }
  </FlexRow>
  );
};

const OptionsCell = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const role = React.useContext(RoleContext);

  const [open, setOpen] = React.useState(false);

  // §2.3 — no edit, no delete, no clone on an MW light row: the caller has no
  // rights on that project beyond checking rooms out.
  if (props.data?.mw_light)
    return null;

  // Task-80 — a group row is edited in the group editor; delete / disband
  // live there too.
  if (props.data?.is_group) {
    if (!(role.admin || role?.privilages?.includes("CreateProjects")))
      return null;

    return (
    <div className="h-full w-full text-right">
      <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle"
            title={t('projects:groups:edit')}
            onClick={() => props.data.requestEditGroup.current(props.data.group_id)}>
        edit_note
      </span>
    </div>
    );
  }
  
  const handleEdit = () => {
    setOpen(false);
    navigate(`/projects/${encodeURIComponent(props.data.id)}`)
  };
  
  const handleDelete = () => {
    setOpen(false);
    props.data.requestDelete.current(props.data.id);
  };

  const handleClone = () => {
    setOpen(false);
    props.data.requestClone.current(props.data.id);
  };

  const handleToggleState = async () => {
    setOpen(false);
    const proj = getProjectById(props.data.id, store.store.getState().projects.projects);
    if (!proj) return;
    const project = copyProjectInfo(proj);
    project.state = (props.data.state === "disabled") ? "enabled" : "disabled";
    const mess = await store.setProject(project);
    checkNoError(mess);
  };
  
  return (
  <Popover key={1}
           isOpen={open}
           onOpenChange={setOpen}
           placement="bottom-end"
           classNames={{
             content: "rounded-none",
           }}>
    <PopoverTrigger>
      <div className="h-full w-full text-right">
        <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle">
          more_vert
        </span>
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[205px] py-[8px] gap-[8px]">
        {(role.admin || role?.privilages?.includes("EditProjectSettings")) &&
            <MenuItem icon="edit_note"
              label={t('projects:edit_project')}
              onClick={handleEdit} />
        }
        {(role.admin || role?.privilages?.includes("CreateProjects")) &&
            <MenuItem icon="content_copy"
              label={t('projects:clone_project')}
              onClick={handleClone} />
        }
      {role.admin &&
        <MenuItem icon={props.data.state === "disabled" ? "public" : "public_off"}
                  label={t(props.data.state === "disabled"
                             ? 'projects:reactivate_project'
                             : 'projects:deactivate_project')}
                  onClick={handleToggleState} />
      }
      {(role.admin || role?.privilages?.includes("DeleteProjects"))&&
        <MenuItem icon="delete"
                  color="!text-[--Warning]"
                  label={t('projects:delete_project:caption')}
                  onClick={handleDelete} />
      }
      </FlexRow>
    </PopoverContent>
  </Popover>
  );
};

export default Projects;
